/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * UatBanner - Thin notification bar for UAT testing progress
 * ============================================================================
 *
 * Displayed above the content area when UAT testing is incomplete.
 * Shows a pulsing dot, progress message, link to the board, and close button.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUatProgress } from '@/hooks/api/useUat';

export function UatBanner() {
  const { t } = useTranslation('platform');
  const { data: progress } = useUatProgress();
  const [dismissed, setDismissed] = useState(false);

  if (!progress || progress.percentComplete >= 100 || dismissed) {
    return null;
  }

  const done = progress.passed + progress.confirmed + progress.closed;

  return (
    <div className="uat-banner" role="status">
      <div className="uat-banner__content">
        <span className="uat-banner__dot" aria-hidden="true" />
        <span className="uat-banner__text">
          {t('uat.banner.message')}{' '}
          {t('uat.banner.progress', { done, total: progress.total })}
        </span>
        <Link to="/venta/uat-testing" className="uat-banner__link">
          {t('uat.banner.viewBoard')}
        </Link>
      </div>
      <button
        className="uat-banner__close"
        onClick={() => setDismissed(true)}
        aria-label={t('uat.banner.dismiss')}
      >
        <X size={14} />
      </button>

      <style>{uatBannerStyles}</style>
    </div>
  );
}

const uatBannerStyles = `
  .uat-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: linear-gradient(
      90deg,
      rgba(0, 240, 255, 0.08),
      rgba(0, 255, 136, 0.05)
    );
    border-bottom: 1px solid rgba(0, 240, 255, 0.1);
    animation: uat-banner-enter 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .uat-banner__content {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .uat-banner__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #00f0ff;
    flex-shrink: 0;
    animation: uat-banner-pulse 2s ease-in-out infinite;
  }

  .uat-banner__text {
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.8);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .uat-banner__link {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #00f0ff;
    text-decoration: none;
    white-space: nowrap;
    flex-shrink: 0;
    transition: color 0.2s ease;
  }

  .uat-banner__link:hover {
    color: #00ff88;
    text-decoration: underline;
  }

  .uat-banner__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    border-radius: 4px;
    flex-shrink: 0;
    transition: color 0.2s ease, background 0.2s ease;
  }

  .uat-banner__close:hover {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.08);
  }

  @keyframes uat-banner-enter {
    from {
      opacity: 0;
      transform: translateY(-100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes uat-banner-pulse {
    0%, 100% {
      box-shadow: 0 0 4px rgba(0, 240, 255, 0.4);
      opacity: 1;
    }
    50% {
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
      opacity: 0.7;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .uat-banner {
      animation: none !important;
    }
    .uat-banner__dot {
      animation: none !important;
    }
  }
`;
